import React, { useState } from 'react';
import {
    Box, Typography, Paper, TextField, Button, CircularProgress, Alert, InputAdornment, IconButton, Link
} from '@mui/material';
import { Visibility, VisibilityOff, Email, Lock } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { loginAdmin } from '../services/Api';
import careoffLogo from '../assets/careoff-logo-2.svg';

const Login = () => {
    const navigate = useNavigate();
    const [formData, setFormData] = useState({ email: '', password: '' });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [showPassword, setShowPassword] = useState(false);

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleLogin = async (e) => {
        e.preventDefault();
        if (!formData.email || !formData.password) {
            setError('Please enter both email and password');
            return;
        }

        setLoading(true);
        setError(null);
        try {
            const response = await loginAdmin(formData);

            // Extract token from response.data.accessToken
            const token = response.data?.accessToken;

            if (token) {
                localStorage.setItem('accessToken', token);
                // Also store user info
                localStorage.setItem('adminUser', JSON.stringify(response.data));
                navigate('/');
            } else {
                setError('Invalid response from server. Token missing.');
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Invalid credentials or server error');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box sx={{
            height: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
            p: 2,
            overflow: 'hidden'
        }}>
            <Paper elevation={3} sx={{
                p: { xs: 3, sm: 4, md: 6 },
                width: '100%',
                maxWidth: 450,
                borderRadius: 3,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                boxShadow: '0 8px 32px rgba(0,0,0,0.1)'
            }}>
                <Box
                    component="img"
                    src={careoffLogo}
                    alt="CareOff"
                    sx={{ height: 48, mb: 2 }}
                    onError={(e) => {
                        e.target.style.display = 'none';
                        const fallback = document.getElementById('fallback-logo');
                        if (fallback) fallback.style.display = 'block';
                    }}
                />
                <Typography id="fallback-logo" variant="h4" fontWeight={700} color="primary" sx={{ display: 'none', mb: 2 }}>
                    CareOff Admin
                </Typography>

                <Typography variant="h5" fontWeight={600} gutterBottom>
                    Welcome Back
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, textAlign: 'center' }}>
                    Sign in to your dashboard to manage the platform
                </Typography>

                {error && <Alert severity="error" sx={{ width: '100%' }}>{error}</Alert>}

                <Box component="form" onSubmit={handleLogin} sx={{ width: '100%' }}>
                    <TextField
                        fullWidth
                        name="email"
                        label="Email Address"
                        variant="outlined"
                        margin="normal"
                        value={formData.email}
                        placeholder='enter admin email....'
                        onChange={handleChange}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <Email color="action" />
                                </InputAdornment>
                            ),
                        }}
                    />
                    <TextField
                        fullWidth
                        name="password"
                        label="Password"
                        type={showPassword ? 'text' : 'password'}
                        variant="outlined"
                        margin="normal"
                        placeholder='enter admin password....'
                        value={formData.password}
                        onChange={handleChange}
                        sx={{ mb: 3 }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <Lock color="action" />
                                </InputAdornment>
                            ),
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                                        {showPassword ? <VisibilityOff /> : <Visibility />}
                                    </IconButton>
                                </InputAdornment>
                            )
                        }}
                    />

                    <Button
                        type="submit"
                        fullWidth
                        variant="contained"
                        size="large"
                        disabled={loading}
                        sx={{
                            mt: 0,
                            mb: 2,
                            py: 1.5,
                            fontSize: '1rem',
                            fontWeight: 600,
                            borderRadius: '8px',
                            textTransform: 'none'
                        }}
                    >
                        {loading ? <CircularProgress size={24} color="inherit" /> : 'Sign In'}
                    </Button>
                </Box>
                <Box textAlign="center" sx={{ mt: 1 }}>
                    <Link
                        component="button"
                        type="button"
                        variant="body2"
                        onClick={() => navigate('/forgot-password')}
                        sx={{
                            color: 'primary.main',
                            textDecoration: 'none',
                            fontWeight: 500,
                            '&:hover': { textDecoration: 'underline' }
                        }}
                    >
                        Forgot Password?
                    </Link>
                </Box>
            </Paper>
        </Box>
    );
};

export default Login;
